import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileSpreadsheet, DollarSign, FileText, Edit3, Save, X, RefreshCw } from "lucide-react";
import { formatCurrency } from "@/lib/utils";
import { useTenantCurrency } from "@/hooks/use-tenant";
import { formatCurrencyWithTenantAndConversion } from "@/lib/currency-conversion";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import ProjectClaimsSummary from "./project-claims-summary";

interface SimpleProjectDetailsProps {
  projectId?: string;
  contractType: string;
  contractAmount: number;
  contractCurrency: string;
  projectHeadline: string;
  projectSummary: string;
  loading?: boolean;
  className?: string;
  showActions?: boolean;
  isSetupMode?: boolean;
  onConfirm?: () => void;
  onReExtract?: () => void;
}

interface ProjectDetailsForm {
  contractType: string;
  contractAmount: string;
  contractCurrency: string;
  projectHeadline: string;
  projectSummary: string;
}

export default function SimpleProjectDetails({
  projectId,
  contractType,
  contractAmount,
  contractCurrency,
  projectHeadline,
  projectSummary,
  loading = false,
  className = "",
  showActions = false,
  isSetupMode = false,
  onConfirm,
  onReExtract
}: SimpleProjectDetailsProps) {
  const { toast } = useToast();
  const { currency: tenantCurrency } = useTenantCurrency();
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<ProjectDetailsForm>({
    contractType,
    contractAmount: contractAmount.toString(),
    contractCurrency,
    projectHeadline,
    projectSummary,
  });

  // Update form data when props change (e.g., after re-extraction)
  useEffect(() => {
    setFormData({
      contractType,
      contractAmount: contractAmount.toString(),
      contractCurrency,
      projectHeadline,
      projectSummary,
    });
  }, [contractType, contractAmount, contractCurrency, projectHeadline, projectSummary]);

  // Mutation to update project details
  const updateProjectMutation = useMutation({
    mutationFn: async (updates: ProjectDetailsForm) => {
      if (!projectId) throw new Error("Project ID is required");
      const response = await apiRequest("PUT", `/api/projects/${projectId}/details`, {
        contractType: updates.contractType,
        contractAmount: parseFloat(updates.contractAmount),
        contractCurrency: updates.contractCurrency,
        projectHeadline: updates.projectHeadline,
        projectSummary: updates.projectSummary,
      });
      return response.json();
    },
    onSuccess: () => {
      // Invalidate project queries to refresh the data
      if (projectId) {
        queryClient.invalidateQueries({ queryKey: [`/api/projects/${projectId}`] });
        queryClient.invalidateQueries({ queryKey: [`/api/projects/${projectId}/documents/analysis`] });
      }
      
      toast({
        title: "Details updated",
        description: "Project details have been successfully updated."
      });
      
      setIsEditing(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to update details",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  const handleInputChange = (field: keyof ProjectDetailsForm, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = () => {
    updateProjectMutation.mutate(formData);
  };

  const handleCancel = () => {
    setFormData({
      contractType,
      contractAmount: contractAmount.toString(),
      contractCurrency,
      projectHeadline,
      projectSummary,
    });
    setIsEditing(false);
  };
  
  // Helper function to get text representation of contract type
  const getContractTypeText = () => {
    return contractType || "Not specified";
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Action buttons - show when showActions is true and not loading */}
      {showActions && !loading && (
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {isEditing ? "Edit Project Details" : "Review Extracted Details"}
            </h3>
            <p className="text-sm text-gray-600">
              {isEditing 
                ? "Review and edit the project details extracted from your documents."
                : "Review the project details extracted from your documents and edit if needed."
              }
            </p>
          </div>
          <div className="flex space-x-2">
            {isEditing ? (
              <>
                <Button 
                  variant="outline" 
                  size="default"
                  onClick={handleCancel}
                  className="flex items-center space-x-2 h-10"
                >
                  <X className="w-4 h-4" />
                  <span>Cancel</span>
                </Button>
                <Button 
                  size="default"
                  onClick={handleSave}
                  disabled={updateProjectMutation.isPending}
                  className="flex items-center space-x-2 h-10"
                >
                  <Save className="w-4 h-4" />
                  <span>{updateProjectMutation.isPending ? "Saving..." : "Save"}</span>
                </Button>
              </>
            ) : (
              <>
                <Button 
                  variant="outline" 
                  size="default"
                  onClick={onReExtract}
                  className="flex items-center space-x-2 h-10"
                >
                  <RefreshCw className="w-4 h-4" />
                  <span>Re-extract Details</span>
                </Button>
                <Button 
                  variant="outline" 
                  size="default"
                  onClick={() => setIsEditing(true)}
                  className="flex items-center space-x-2 h-10"
                >
                  <Edit3 className="w-4 h-4" />
                  <span>Edit Details</span>
                </Button>
                <Button
                  size="default"
                  onClick={onConfirm}
                  className="bg-green-600 hover:bg-green-700 text-white flex items-center space-x-2 h-10"
                >
                  <span>Confirm & Activate Project</span>
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Project Details Cards */}
      <div className="space-y-4">
        {/* Top Row: Contract Type, Contract Amount, Project Description */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Contract Type Card */}
          <Card className="bg-white shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center">
                <FileSpreadsheet className="w-4 h-4 mr-2 text-blue-600" />
                Contract Type
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {loading ? (
                <div className="bg-muted animate-pulse rounded-md h-6 w-24" />
              ) : isEditing ? (
                <div className="space-y-2">
                  <Label htmlFor="contractType" className="text-xs text-gray-500">
                    Select contract type
                  </Label>
                  <Select 
                    value={formData.contractType} 
                    onValueChange={(value) => handleInputChange('contractType', value)}
                  >
                    <SelectTrigger id="contractType">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Fixed Price">Fixed Price</SelectItem>
                      <SelectItem value="Time & Materials">Time & Materials</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="text-lg font-semibold text-gray-900">
                  {getContractTypeText()}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Contract Amount Card */}
          <Card className="bg-white shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center">
                <DollarSign className="w-4 h-4 mr-2 text-green-600" />
                Contract Amount
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {loading ? (
                <div className="bg-muted animate-pulse rounded-md h-6 w-32" />
              ) : isEditing ? (
                <div className="space-y-2">
                  <Label htmlFor="contractAmount" className="text-xs text-gray-500">
                    Contract value and currency
                  </Label>
                  <div className="flex space-x-2">
                    <Input
                      id="contractAmount"
                      type="number"
                      step="0.01"
                      value={formData.contractAmount}
                      onChange={(e) => handleInputChange('contractAmount', e.target.value)}
                      className="flex-1"
                      placeholder="0.00"
                    />
                    <Select 
                      value={formData.contractCurrency} 
                      onValueChange={(value) => handleInputChange('contractCurrency', value)}
                    >
                      <SelectTrigger className="w-20">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="EUR">EUR</SelectItem>
                        <SelectItem value="GBP">GBP</SelectItem>
                        <SelectItem value="CAD">CAD</SelectItem>
                        <SelectItem value="AUD">AUD</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="text-lg font-semibold text-gray-900">
                    {formatCurrencyWithTenantAndConversion(contractAmount, contractCurrency, tenantCurrency, {
                      showSymbol: true,
                      showOriginal: false,
                      showConversionRate: false
                    })}
                  </div>
                  {contractCurrency !== tenantCurrency && (
                    <div className="text-xs text-blue-600 mt-1">
                      💱 Converted from {contractCurrency}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Project Description Card */}
          <Card className="bg-white shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center">
                <FileText className="w-4 h-4 mr-2 text-purple-600" />
                Project Description
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {loading ? (
                <div className="bg-muted animate-pulse rounded-md h-6 w-full" />
              ) : isEditing ? (
                <div className="space-y-2">
                  <Label htmlFor="projectHeadline" className="text-xs text-gray-500">
                    Brief project description (one line)
                  </Label>
                  <Input
                    id="projectHeadline"
                    value={formData.projectHeadline}
                    onChange={(e) => handleInputChange('projectHeadline', e.target.value)}
                    placeholder="Enter project description..."
                    className="w-full"
                  />
                </div>
              ) : (
                <div className="text-sm text-gray-700 leading-relaxed">
                  {projectHeadline || "No description provided"}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Bottom Row: Project Summary (Full Width) */}
        <Card className="bg-white shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center">
              <FileText className="w-4 h-4 mr-2 text-orange-600" />
              Project Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {loading ? (
              <div className="space-y-2">
                <div className="bg-muted animate-pulse rounded-md h-4 w-full" />
                <div className="bg-muted animate-pulse rounded-md h-4 w-3/4" />
                <div className="bg-muted animate-pulse rounded-md h-4 w-5/6" />
              </div>
            ) : isEditing ? (
              <div className="space-y-2">
                <Label htmlFor="projectSummary" className="text-xs text-gray-500">
                  Detailed project summary (paragraph)
                </Label>
                <Textarea
                  id="projectSummary"
                  value={formData.projectSummary}
                  onChange={(e) => handleInputChange('projectSummary', e.target.value)}
                  placeholder="Enter detailed project summary..."
                  className="w-full h-24 resize-none"
                />
              </div>
            ) : (
              <div className="text-sm text-gray-700 leading-relaxed">
                {projectSummary || projectHeadline || "No details provided"}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Claims Summary Section - only show when not in setup mode and has project ID */}
        {!isSetupMode && projectId && projectId !== 'new' && (
          <ProjectClaimsSummary projectId={projectId} loading={loading} />
        )}
      </div>
    </div>
  );
}